import { Button, Card, Divider, Highlight, Typography, Body, Stack } from '@optimus-web/core';
import { OpenPayout, PayoutSummary } from '@mews-commander/data-access';
import { LocalizedLabel, LocalizedSmallParagraph } from '@mews-commander/ui';
import { LocalizedCurrencyValue, LocalizedText, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { createUUID } from '@mews/std';
import { PAYOUT_STATE_TEXT_KEY_MAP } from '../textKeyMaps';

interface PayoutSummaryCardProps {
    payoutSummary: PayoutSummary | null;
    openPayout: OpenPayout;
    redirectToOpenPayout: (currencyCode: string) => void;
}

const OpenPayoutDetail = ({
    openPayout: { net },
    redirectToOpenPayout,
}: Omit<PayoutSummaryCardProps, 'payoutSummary'>) => (
    <Stack vertical spacing="100">
        <LocalizedLabel textKey={TextKey.PayoutOpenBalance} as="div" />
        <Stack distribution="space-between" itemAlignment="center">
            <Highlight size="small">
                <LocalizedCurrencyValue value={{ value: net.value, currency: net.currencyCode }} />
            </Highlight>
            <Button onClick={() => redirectToOpenPayout(net.currencyCode)} type="submit">
                <LocalizedText textKey={TextKey.PayoutOpenBalanceDetailButtonText} />
            </Button>
        </Stack>
    </Stack>
);

const PayoutSummaryDetail = ({ payoutSummary: { stateAmounts, totalNet } }: { payoutSummary: PayoutSummary }) => (
    <Stack vertical spacing="100">
        <Typography textStyle="bodyMediumStrong" as="div">
            {totalNet.currencyCode}
            {UnicodeEntity.NoBreakSpace}
            <LocalizedText textKey={TextKey.PayoutNetPaidOut} />
        </Typography>
        <Stack vertical spacing="200">
            <Highlight size="small">
                <LocalizedCurrencyValue value={{ value: Math.abs(totalNet.value), currency: totalNet.currencyCode }} />
            </Highlight>
            <Stack vertical spacing="100">
                {stateAmounts.map(({ state, net }) => (
                    <Stack distribution="space-between" key={createUUID()}>
                        <LocalizedSmallParagraph textKey={PAYOUT_STATE_TEXT_KEY_MAP[state]} variant="secondary" />
                        <Body size="small">
                            <LocalizedCurrencyValue
                                value={{ value: Math.abs(net.value), currency: net.currencyCode }}
                            />
                        </Body>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    </Stack>
);

export const PayoutSummaryCard = ({ payoutSummary, openPayout, redirectToOpenPayout }: PayoutSummaryCardProps) => {
    const openBalanceEnabled = useFeatureFlag(FeatureFlag.openBalanceEnabled);

    if (!payoutSummary && !openBalanceEnabled) return null;

    return (
        <Card padding="200">
            <Stack vertical spacing="200">
                {payoutSummary && <PayoutSummaryDetail payoutSummary={payoutSummary} />}
                {openBalanceEnabled && (
                    <>
                        {payoutSummary && <Divider />}
                        {openPayout && (
                            <OpenPayoutDetail openPayout={openPayout} redirectToOpenPayout={redirectToOpenPayout} />
                        )}
                    </>
                )}
            </Stack>
        </Card>
    );
};
